var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from './api';
import { getNewStateByAttr, getNewStateById } from './entities';
import { fetchFantasyEventGames } from './fantasyEventGames';
import { fetchFantasyEvents } from './fantasyEvents';
import { fetchMarketEvents } from './marketEvents';
import { isTicketFulfilledAction } from './tickets';
const initialState = {
    byCloseDate: {},
    byCloseTs: {},
    byEventGamesId: {},
    byFinalizeTs: {},
    byGameType: {},
    byId: {},
    byLeague: {},
    byOpenTs: {},
    byStatus: {},
    status: 'init'
};
export const fetchEvent = createAsyncThunk('events/detail/fetch', async (eventId, thunkApi) => {
    try {
        return await (await api.instance).fetchEvent(eventId);
    }
    catch (error) {
        return thunkApi.rejectWithValue(error);
    }
});
export const fetchEvents = createAsyncThunk('events/fetch', async (arg, thunkApi) => {
    const { eventGamesIds, gameType = 'fantasy_stats' } = arg, argOptions = __rest(arg, ["eventGamesIds", "gameType"]);
    const options = Object.assign(Object.assign({}, argOptions), { gameType });
    if (eventGamesIds === null || eventGamesIds === void 0 ? void 0 : eventGamesIds.length) {
        options.externalIdIn = eventGamesIds.map((eventGamesId) => String(eventGamesId));
    }
    try {
        return await (await api.instance).fetchEvents(options);
    }
    catch (error) {
        return thunkApi.rejectWithValue(error);
    }
});
export const fetchClosedEvents = (eventGamesIds, gameType) => {
    return fetchEvents({ eventGamesIds, gameType, status: 'c' });
};
export const fetchFinalizedEvents = (eventGamesIds, gameType) => {
    return fetchEvents({ eventGamesIds, gameType, status: 'f' });
};
export const fetchOpenEvents = (eventGamesIds, gameType) => {
    return fetchEvents({ eventGamesIds, gameType, status: 'o' });
};
export const fetchOpenAndClosedEvents = (eventGamesIds, gameType, showAppHidden) => {
    return fetchEvents({ eventGamesIds, gameType, showAppHidden, statusIn: ['o', 'c'] });
};
const getNewStateByDateAttrPredicate = (event) => event.closeTs ? event.closeTs.split('T')[0] : undefined;
const applyNewEventsState = (state, events) => {
    state.byId = getNewStateById(state.byId, events);
    state.byCloseDate = getNewStateByAttr(state.byId, getNewStateByDateAttrPredicate);
    state.byCloseTs = getNewStateByAttr(state.byId, 'closeTs');
    state.byFinalizeTs = getNewStateByAttr(state.byId, 'finalizeTs');
    state.byGameType = getNewStateByAttr(state.byId, 'gameType');
    state.byOpenTs = getNewStateByAttr(state.byId, 'openTs');
    state.byEventGamesId = getNewStateByAttr(state.byId, 'externalId');
    state.byLeague = getNewStateByAttr(state.byId, 'context');
    state.byStatus = getNewStateByAttr(state.byId, 'status');
};
function isFetchingAction(action) {
    return action.type.startsWith('events/') && action.type.endsWith('/pending');
}
function isRejectedAction(action) {
    return action.type.startsWith('events/') && action.type.endsWith('/rejected');
}
const eventsSlice = createSlice({
    name: 'events',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchEvent.fulfilled, (state, action) => {
            applyNewEventsState(state, [action.payload]);
            state.status = 'success';
        })
            .addCase(fetchEvents.fulfilled, (state, action) => {
            applyNewEventsState(state, action.payload);
            state.status = 'success';
        })
            .addCase(fetchMarketEvents.fulfilled, (state, action) => {
            applyNewEventsState(state, action.payload.events);
        })
            .addCase(fetchFantasyEvents.fulfilled, (state, action) => {
            applyNewEventsState(state, [action.payload.event]);
        })
            .addCase(fetchFantasyEventGames.fulfilled, (state, action) => {
            action.payload.forEach((fantasyEventGame) => {
                if (fantasyEventGame.event) {
                    applyNewEventsState(state, [fantasyEventGame.event]);
                }
            });
        })
            .addMatcher(isTicketFulfilledAction, (state, action) => {
            if (typeof action.payload.events !== 'undefined') {
                applyNewEventsState(state, action.payload.events);
            }
        })
            .addMatcher(isFetchingAction, (state) => {
            state.status = 'fetching';
        })
            .addMatcher(isRejectedAction, (state) => {
            state.status = 'error';
        });
    }
});
export const eventsReducer = eventsSlice.reducer;
